import React from 'react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Helmet } from 'react-helmet-async';
import { getCanonicalUrl } from '@/lib/publicBaseUrl';
import { CommitmentLadder } from '@/components/shared/CommitmentLadder';
import { Placeholder } from '@/components/shared/Placeholder';
import { CheckCircle2, User, Heart, Compass } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SocialShare } from '@/components/ui/SocialShare';
import { LazyImage } from '@/components/ui/LazyImage';


export function PersonalMasteryProductPage() {
  return (
    <main className="pt-32 pb-20 bg-ground-alt min-h-screen">
      <SocialShare title="Personal Mastery & Resilience | FIVE LENS Enneagram" />
      <Helmet>
        <title>Personal Mastery & Resilience | Leadership Development | FIVE LENS</title>
        <meta name="description" content="Build emotional resilience and leadership maturity. The FIVE LENS approach measures vertical development, self-efficacy, and adaptive capacity under pressure." />
        <meta property="og:title" content="Personal Mastery & Resilience | Leadership Development | FIVE LENS" />
        <meta property="og:description" content="Build emotional resilience and leadership maturity. The FIVE LENS approach measures vertical development, self-efficacy, and adaptive capacity under pressure." />
        <meta name="twitter:title" content="Personal Mastery & Resilience | Leadership Development | FIVE LENS" />
        <meta name="twitter:description" content="Build emotional resilience and leadership maturity. The FIVE LENS approach measures vertical development, self-efficacy, and adaptive capacity under pressure." />
        <link rel="canonical" href={getCanonicalUrl('/personal-mastery-and-resilience')} />
      </Helmet>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 mb-8">
        <Breadcrumb items={[{ label: 'For Organizations & HR Teams', href: '/organizations' }, { label: 'Personal Mastery & Resilience' }]} />
      </div>
<div className="max-w-7xl mx-auto px-6 lg:px-12 mb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-4 block">Standalone Product</span>
            <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-6">Personal Mastery &amp; Resilience</h1>
            <p className="text-xl text-ink/80 mb-8 leading-relaxed">
              Equip your people with the profound self-awareness and emotional agility required to thrive in complex, high-pressure environments.
            </p>
            <div className="space-y-4 mb-8">
              {[
                'Individualised 45-page Personal Mastery Report',
                'Deep-dive Resilience and Stress Response profiling',
                'Actionable development pathways for each individual',
                'Facilitated half-day or full-day workshops available'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-ink/80">{item}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex justify-center bg-ink text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-rule-3 transition-colors"
              >
                Enquire About Pricing
              </Link>
              <Link
                to="/reports/premium"
                className="inline-flex justify-center border border-ink text-ink px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-ground-alt transition-colors"
              >
                View Premium Report
              </Link>
            </div>
          </div>
          <div className="bg-white p-2 border border-ground-deep relative">
            <LazyImage 
              src="/five_lens_personal_mastery_web.webp" 
              alt="Professionals engaged in a leadership workshop" 
              className="w-full h-full object-cover aspect-[4/3]"
            />
          </div>
        </div>
      </div>

      <section className="bg-white py-24 border-y border-ground-deep">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-serif text-ink mb-6">Beyond Traditional Assessments</h2>
          <p className="text-lg text-ink/70 leading-relaxed">
            The Personal Mastery and Resilience product leverages the full FIVE LENS Enneagram framework, isolating the layers most critical to individual performance, psychological safety, and emotional regulation.
          </p>
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-8 text-left mb-24">
            <div className="p-8 border border-ground-deep hover:border-rule-3 transition-colors">
              <User className="w-8 h-8 text-rule-3 mb-6" />
              <h3 className="text-xl font-serif text-ink mb-3">Self-Awareness</h3>
              <p className="text-sm text-ink/70">
                Identify blind spots and habitual defense mechanisms that limit professional effectiveness, communication clarity, and career growth.
              </p>
            </div>
            <div className="p-8 border border-ground-deep hover:border-rule-3 transition-colors">
              <Heart className="w-8 h-8 text-rule-3 mb-6" />
              <h3 className="text-xl font-serif text-ink mb-3">Emotional Agility</h3>
              <p className="text-sm text-ink/70">
                Learn to recognize physiological and emotional triggers, creating the space to consciously choose constructive responses during conflict or intense stress.
              </p>
            </div>
            <div className="p-8 border border-ground-deep hover:border-rule-3 transition-colors">
              <Compass className="w-8 h-8 text-rule-3 mb-6" />
              <h3 className="text-xl font-serif text-ink mb-3">Resilience Capacity</h3>
              <p className="text-sm text-ink/70">
                Build a sustainable, systemic capacity to absorb disruption, recover from setbacks, and maintain strategic focus during periods of rapid organizational change.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-serif text-ink mb-6">The True Cost of Low Resilience</h2>
              <p className="text-ink/70/80 leading-relaxed mb-6">
                In today's hyper-complex business environment, technical competence is no longer sufficient for leadership success. When individuals lack personal mastery and emotional resilience, the organizational costs are immediate and severe:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-ground-deep/50 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 bg-ink rounded-full"></span>
                  </div>
                  <span className="text-sm text-ink/70/80"><strong>Reactive Decision-Making:</strong> Leaders default to short-term, fear-based choices rather than strategic, long-term vision.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-ground-deep/50 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 bg-ink rounded-full"></span>
                  </div>
                  <span className="text-sm text-ink/70/80"><strong>Burnout & Attrition:</strong> High-performers exhaust themselves through poor boundary management and inability to regulate their nervous system.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-ground-deep/50 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 bg-ink rounded-full"></span>
                  </div>
                  <span className="text-sm text-ink/70/80"><strong>Cultural Toxicity:</strong> Unmanaged stress cascades down the organizational chart, destroying psychological safety and trust within teams.</span>
                </li>
              </ul>
            </div>
            <div className="bg-ground p-10 relative">
              <h3 className="text-2xl font-serif text-ink mb-6">Program Applications</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-2">High-Potential Development</h4>
                  <p className="text-sm text-ink/70/70">Prepare individual contributors for the emotional demands of people management and leadership roles.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-2">Executive Coaching</h4>
                  <p className="text-sm text-ink/70/70">Provide senior leaders with the deep self-insight necessary to break career-limiting behavioral patterns.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-2">Change Management</h4>
                  <p className="text-sm text-ink/70/70">Equip entire departments with a shared vocabulary for managing stress during mergers, restructuring, or hyper-growth.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CommitmentLadder context="general" />
    </main>
  );
}
